import { Fragment, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ChangeEvent, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { DndContext, PointerSensor, pointerWithin, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { CalendarRange, Check, Copy, Eye, Minus, MoreHorizontal, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react'
import { gridGenerationsApi, gridTemplatesApi, liveSourcesApi, programsApi, seriesApi, type TemplateRollOptions, type TemplateSlotWrite } from '../../lib/api.js'
import { useActiveChannelId, useAgendaView, useHasPermission, useStore } from '../../store/index.js'
import { PPMW_MAX, PPMW_MIN, SLIDER_MAX, SLIDER_MIN, clampPpmw, formatPpm, ppmToSlider, sliderToPpm } from '../agenda/useAgendaLayout.js'
import { TemplateHourAxis, templateCanvasVars } from './TemplateHourAxis.js'
import type {
  GridGeneration,
  LiveSource,
  Program,
  ScheduleGrid,
  Series,
  TemplateRuleType,
  TemplateSlot,
  TemplateSlotEntry,
} from '../../types/domain.js'
import { TemplatePreviewWorkspace } from './TemplatePreviewWorkspace.js'
import { TemplateRetimeDialog, TemplateSmartFillDialog, type TemplateSlotDraft } from './TemplateAssistDialogs.js'
import { ContextMenu, isEditableKeyboardTarget } from '../common/ContextMenu.js'
import { PageHeader } from '../common/PageChrome.js'
import { formatAdSpotLabel } from '../../lib/adSpotLabel.js'
import { broadcastDayKey } from '../../lib/broadcast-day.js'
import { getServerNow } from '../../hooks/useServerClock.js'
import { MONTH_CYCLE_WEEK_NUMBERS, isFifthWeekOnly, nextFifthMonday, upcomingCycleMondays } from '../../lib/monthCycleWeeks.js'
import './GridTemplatesView.css'

/** Spot de la régie tel que le sélecteur de pre-roll / post-roll en a besoin. */
type RollSpot = TemplateRollOptions['adSpots'][number]
type RollProgram = TemplateRollOptions['jingles'][number]

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche']
const TEMPLATE_FILTERS = [
  ['active', 'Actifs'],
  ['archived', 'Archivés'],
  ['all', 'Tous'],
] as const
/** 0 = toutes les semaines, sinon la semaine du cycle mensuel (1 à 4). */
type WeekFilter = 0 | 1 | 2 | 3 | 4
const WEEK_FILTERS: readonly WeekFilter[] = [0, 1, 2, 3, 4]

function timeToMinutes(value: string) {
  const [hours, minutes] = value.split(':').map(Number)
  return hours * 60 + minutes
}

function minutesToTime(value: number) {
  const normalized = Math.max(0, Math.min(1439, Math.round(value / 15) * 15))
  return `${String(Math.floor(normalized / 60)).padStart(2, '0')}:${String(normalized % 60).padStart(2, '0')}:00`
}

/**
 * Place côte à côte les créneaux qui se chevauchent réellement (indépendamment
 * de `cycleWeeks`, qui n'entre pas en jeu ici : le filtre par semaine a déjà
 * retiré ce qui ne peut jamais coexister).
 *
 * Le chevauchement se raisonne en **jour + heure-de-la-journée**, jamais sur une
 * timeline absolue continue : un créneau `daySpan > 1` (ex. le JT quotidien posé
 * une fois pour lundi→vendredi) occupe la même tranche horaire *sur chaque jour*,
 * pas la nuit entre les jours. Deux créneaux sont donc en conflit si leurs plages
 * de jours **et** leurs plages horaires se recoupent. On colore ensuite le graphe
 * de conflits par composante connexe, chaque composante partageant son `laneCount`
 * pour que les largeurs restent uniformes dans une même grappe visuelle.
 */
function assignSlotLanes(slots: Pick<TemplateSlot, 'id' | 'dayOffset' | 'daySpan' | 'timeOfDay' | 'nominalDurationSec'>[]) {
  const nodes = slots.map(slot => {
    const dayStart = slot.dayOffset
    const dayEnd = slot.dayOffset + slot.daySpan // demi-ouvert
    const timeStart = timeToMinutes(slot.timeOfDay)
    const timeEnd = timeStart + slot.nominalDurationSec / 60
    return { id: slot.id, dayStart, dayEnd, timeStart, timeEnd, lane: -1 }
  })

  const conflicts = (a: typeof nodes[number], b: typeof nodes[number]) =>
    a.dayStart < b.dayEnd && b.dayStart < a.dayEnd && a.timeStart < b.timeEnd && b.timeStart < a.timeEnd

  // Ordre déterministe : par heure de début, puis par jour, puis par durée.
  const ordered = [...nodes].sort((a, b) =>
    a.timeStart - b.timeStart || a.dayStart - b.dayStart || (a.timeEnd - a.timeStart) - (b.timeEnd - b.timeStart))

  // Coloration gloutonne : plus petite voie libre parmi les voisins déjà placés.
  for (const node of ordered) {
    const taken = new Set(ordered.filter(other => other.lane >= 0 && conflicts(node, other)).map(other => other.lane))
    let lane = 0
    while (taken.has(lane)) lane += 1
    node.lane = lane
  }

  // Composantes connexes du graphe de conflits pour partager le laneCount.
  const componentOf = new Map<string, number>()
  let nextComponent = 0
  for (const node of nodes) {
    if (componentOf.has(node.id)) continue
    const stack = [node]
    componentOf.set(node.id, nextComponent)
    while (stack.length) {
      const current = stack.pop()!
      for (const other of nodes) {
        if (componentOf.has(other.id) || !conflicts(current, other)) continue
        componentOf.set(other.id, nextComponent)
        stack.push(other)
      }
    }
    nextComponent += 1
  }

  const componentLaneCount = new Map<number, number>()
  for (const node of nodes) {
    const component = componentOf.get(node.id)!
    componentLaneCount.set(component, Math.max(componentLaneCount.get(component) ?? 1, node.lane + 1))
  }

  const lanes = new Map<string, { lane: number; laneCount: number }>()
  for (const node of nodes) {
    lanes.set(node.id, { lane: node.lane, laneCount: componentLaneCount.get(componentOf.get(node.id)!)! })
  }
  return lanes
}

function averageProgramDurationSec(programs: Program[], matches: (program: Program) => boolean): number | null {
  const durations = programs
    .filter(program => !program.parentProgramId && matches(program))
    .map(program => program.blockDurationSec ?? program.totalDurationSec ?? (
      program.durationSec + (program.parts ?? []).reduce((sum, part) => sum + part.durationSec, 0) +
      program.interPartBreakSec * (program.parts?.length ?? 0)
    ))
    .filter(duration => duration > 0)
  return durations.length ? Math.round(durations.reduce((sum, duration) => sum + duration, 0) / durations.length) : null
}

export const GridTemplatesView = memo(function GridTemplatesView() {
  const channelId = useActiveChannelId()
  const agendaView = useAgendaView()
  const canManage = useHasPermission('GRID_TEMPLATE_MANAGE')
  const [templates, setTemplates] = useState<ScheduleGrid[]>([])
  const [archiveFilter, setArchiveFilter] = useState<'active' | 'archived' | 'all'>('active')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [template, setTemplate] = useState<(ScheduleGrid & { templateSlots: TemplateSlot[] }) | null>(null)
  const [selectedSlotId, setSelectedSlotId] = useState<string | null>(null)
  // Selection multiple : Ctrl/Cmd ou Maj + clic ajoute au lot.
  const [checkedSlotIds, setCheckedSlotIds] = useState<Set<string>>(new Set())
  const [slotMenu, setSlotMenu] = useState<{ slot: TemplateSlot; x: number; y: number } | null>(null)
  const [previewMode, setPreviewMode] = useState(false)
  const [programs, setPrograms] = useState<Program[]>([])
  // Habillages et spots proposés en pre-roll / post-roll d'un créneau.
  const [jingles, setJingles] = useState<RollProgram[]>([])
  const [adSpots, setAdSpots] = useState<RollSpot[]>([])
  const [series, setSeries] = useState<Series[]>([])
  const [liveSources, setLiveSources] = useState<LiveSource[]>([])
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [assistDialog, setAssistDialog] = useState<'SMART' | 'RETIME' | null>(null)
  const [generations, setGenerations] = useState<GridGeneration[]>([])
  const [inspectorMode, setInspectorMode] = useState<'GENERATION' | 'RULE'>('GENERATION')
  // Vue par semaine du cycle mensuel : masque les créneaux qui ne jouent pas
  // cette semaine-là (cycleWeeks vide = toujours visible).
  const [weekFilter, setWeekFilter] = useState<WeekFilter>(0)
  const canvasScrollRef = useRef<HTMLDivElement>(null)
  const hourHeight = clampPpmw(agendaView.pxPerMinuteWeek) * 60
  const previousHourHeightRef = useRef(hourHeight)
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  useLayoutEffect(() => {
    const scroll = canvasScrollRef.current
    const previousHourHeight = previousHourHeightRef.current
    previousHourHeightRef.current = hourHeight
    if (!scroll || previousHourHeight === hourHeight) return
    const previousCenter = scroll.scrollTop + scroll.clientHeight / 2
    scroll.scrollTop = Math.max(0, previousCenter * hourHeight / previousHourHeight - scroll.clientHeight / 2)
  }, [hourHeight])

  const loadTemplates = useCallback(async () => {
    if (!channelId) return
    const list = await gridTemplatesApi.list(channelId, archiveFilter)
    setTemplates(list)
    setSelectedId(current => current && list.some(item => item.id === current) ? current : list[0]?.id ?? null)
  }, [channelId, archiveFilter])

  const loadTemplate = useCallback(async (id: string) => {
    const detail = await gridTemplatesApi.getById(id)
    setTemplate(detail)
    setSelectedSlotId(current => current && detail.templateSlots.some(item => item.id === current) ? current : null)
  }, [])

  const loadGenerations = useCallback(async () => {
    if (!channelId) return
    setGenerations(await gridGenerationsApi.recent(channelId))
  }, [channelId])

  useEffect(() => {
    if (!channelId) return
    loadTemplates().catch(console.error)
    loadGenerations().catch(console.error)
    Promise.all([
      programsApi.list({ channelId, limit: 100 }),
      seriesApi.list({ channelId }),
      liveSourcesApi.list(channelId),
    ]).then(([programResult, seriesResult, liveSourceResult]) => {
      setPrograms(programResult.data)
      setSeries(seriesResult)
      setLiveSources(liveSourceResult)
    }).catch(console.error)
    // Les choix de rolls appartiennent au modèle de grille : l'API ne renvoie
    // que les habillages actifs et diffusables, ainsi que les spots actifs,
    // sans exiger les droits séparés des écrans Jingles et Régie publicitaire.
    gridTemplatesApi.rollOptions(channelId)
      .then(result => {
        setJingles(result.jingles)
        setAdSpots(result.adSpots)
      })
      .catch(() => {
        setJingles([])
        setAdSpots([])
      })
  }, [channelId, loadGenerations, loadTemplates])

  useEffect(() => {
    setPreviewMode(false)
    setInspectorMode('GENERATION')
    if (selectedId) loadTemplate(selectedId).catch(console.error)
    else setTemplate(null)
  }, [selectedId, loadTemplate])

  const selectedSlot = useMemo(
    () => template?.templateSlots.find(item => item.id === selectedSlotId) ?? null,
    [template, selectedSlotId],
  )
  const createTemplate = useCallback(async () => {
    if (!channelId) return
    const name = window.prompt('Nom du modèle hebdomadaire')
    if (!name?.trim()) return
    const created = await gridTemplatesApi.create({ channelId, name: name.trim() })
    await loadTemplates()
    setSelectedId(created.id)
  }, [channelId, loadTemplates])

  const addSlot = useCallback(async () => {
    if (!template) return
    const firstSeries = series[0]
    const firstProgram = programs[0]
    if (!firstSeries && !firstProgram) {
      setMessage('Ajoutez d’abord un programme ou une série au catalogue.')
      return
    }
    const averageDuration = firstSeries
      ? averageProgramDurationSec(programs, program => program.seriesId === firstSeries.id)
      : null
    const created = await gridTemplatesApi.addSlot(template.id, {
      dayOffset: 0,
      daySpan: 1,
      timeOfDay: '18:00:00',
      nominalDurationSec: averageDuration ?? firstProgram?.blockDurationSec ?? firstProgram?.durationSec ?? 1800,
      episodeSpacingSec: 0,
      ruleType: firstSeries ? 'PREMIERE' : 'FIXED_PROGRAM',
      airingKind: firstSeries ? 'PREMIERE' : 'STANDARD',
      contentAlignment: 'START',
      fixedProgramId: firstSeries ? null : firstProgram?.id ?? null,
      seriesId: firstSeries?.id ?? null,
      liveSourceId: null,
      liveStartMode: 'AUTO',
      liveEndMode: 'AUTO',
      preRollProgramId: null,
      preRollAdSpotId: null,
      postRollProgramId: null,
      postRollAdSpotId: null,
      minRerunGapDays: null,
      maxRerunsTotal: null,
      cycleWeeks: [],
      // Un créneau neuf est ordinaire : il se décale si ce qui le précède
      // déborde. L'opérateur coche lui-même ses rendez-vous d'antenne.
      anchored: false,
      mirrorSlotId: null,
      sortOrder: template.templateSlots.length,
    })
    await loadTemplate(template.id)
    setSelectedSlotId(created.id)
  }, [template, series, programs, loadTemplate])

  const updateSlot = useCallback(async (slotId: string, data: TemplateSlotWrite) => {
    if (!template) return
    await gridTemplatesApi.updateSlot(slotId, data)
    await loadTemplate(template.id)
  }, [template, loadTemplate])

  const addAssistedSlots = useCallback(async (slots: TemplateSlotDraft[]) => {
    if (!template) return
    setBusy(true)
    setMessage(null)
    try {
      await gridTemplatesApi.addSlots(template.id, slots)
      await loadTemplate(template.id)
      await loadTemplates()
      setMessage(`${slots.length} créneau(x) ajouté(s) au modèle. Vérifiez puis validez la nouvelle version.`)
    } finally {
      setBusy(false)
    }
  }, [loadTemplate, loadTemplates, template])

  const deleteSpecificSlot = useCallback(async (slot: TemplateSlot) => {
    if (!template) return
    const label = slot.fixedProgram?.title ?? slot.series?.title ?? 'ce créneau'
    if (!window.confirm(`Supprimer « ${label} » du modèle ?`)) return
    await gridTemplatesApi.deleteSlot(slot.id)
    setSelectedSlotId(null)
    await loadTemplate(template.id)
  }, [template, loadTemplate])

  const deleteSlot = useCallback(async () => {
    if (!selectedSlot) return
    await deleteSpecificSlot(selectedSlot)
  }, [deleteSpecificSlot, selectedSlot])

  const toggleSlotChecked = useCallback((slotId: string) => {
    setCheckedSlotIds(current => {
      const next = new Set(current)
      if (next.has(slotId)) next.delete(slotId)
      else next.add(slotId)
      return next
    })
  }, [])

  const clearCheckedSlots = useCallback(() => setCheckedSlotIds(new Set()), [])

  const deleteCheckedSlots = useCallback(async () => {
    if (!template || checkedSlotIds.size === 0) return
    const ids = [...checkedSlotIds]
    if (!window.confirm(`Supprimer ${ids.length} créneau(x) du modèle ?`)) return
    // Suppression séquentielle : l'API n'expose pas de suppression groupée, et
    // enchaîner en parallèle ferait perdre la trace de ce qui a réellement
    // échoué si l'un des créneaux est déjà parti.
    const failed: string[] = []
    for (const id of ids) {
      try {
        await gridTemplatesApi.deleteSlot(id)
      } catch {
        failed.push(id)
      }
    }
    setCheckedSlotIds(new Set())
    setSelectedSlotId(null)
    await loadTemplate(template.id)
    setMessage(failed.length === 0
      ? `${ids.length} créneau(x) supprimé(s)`
      : `${ids.length - failed.length} supprimé(s), ${failed.length} en échec`)
  }, [checkedSlotIds, loadTemplate, template])

  const duplicateSlot = useCallback(async (slot: TemplateSlot) => {
    if (!template || template.status === 'ARCHIVED') return
    const endMinutes = timeToMinutes(slot.timeOfDay) + Math.ceil(slot.nominalDurationSec / 60)
    const dayShift = Math.floor(endMinutes / 1440)
    const canRoll = slot.dayOffset + dayShift <= 6
    const created = await gridTemplatesApi.addSlot(template.id, {
      dayOffset: canRoll ? slot.dayOffset + dayShift : slot.dayOffset,
      daySpan: Math.min(slot.daySpan, 7 - (canRoll ? slot.dayOffset + dayShift : slot.dayOffset)),
      timeOfDay: canRoll ? minutesToTime(endMinutes % 1440) : slot.timeOfDay,
      nominalDurationSec: slot.nominalDurationSec,
      episodeSpacingSec: slot.episodeSpacingSec,
      ruleType: slot.ruleType,
      airingKind: slot.airingKind,
      contentAlignment: slot.contentAlignment,
      fixedProgramId: slot.fixedProgramId,
      seriesId: slot.seriesId,
      liveSourceId: slot.liveSourceId,
      liveStartMode: slot.liveStartMode,
      liveEndMode: slot.liveEndMode,
      preRollProgramId: slot.preRollProgramId,
      preRollAdSpotId: slot.preRollAdSpotId,
      postRollProgramId: slot.postRollProgramId,
      postRollAdSpotId: slot.postRollAdSpotId,
      minRerunGapDays: slot.minRerunGapDays,
      maxRerunsTotal: slot.maxRerunsTotal,
      cycleWeeks: slot.cycleWeeks,
      anchored: slot.anchored,
      mirrorSlotId: slot.mirrorSlotId,
      sortOrder: Math.max(-1, ...template.templateSlots.map(item => item.sortOrder)) + 1,
    })
    await loadTemplate(template.id)
    await loadTemplates()
    setSelectedSlotId(created.id)
    setMessage('Créneau dupliqué après l’élément source. Vérifiez son horaire avant de valider le modèle.')
  }, [loadTemplate, loadTemplates, template])

  useEffect(() => {
    const handleBackspace = (event: KeyboardEvent) => {
      if (event.key !== 'Backspace' || event.repeat || isEditableKeyboardTarget(event.target)) return
      if (!selectedSlot || template?.status === 'ARCHIVED') return
      event.preventDefault()
      deleteSpecificSlot(selectedSlot).catch(console.error)
    }
    window.addEventListener('keydown', handleBackspace)
    return () => window.removeEventListener('keydown', handleBackspace)
  }, [deleteSpecificSlot, selectedSlot, template?.status])

  const validateTemplate = useCallback(async () => {
    if (!template) return
    setBusy(true)
    try {
      const result = await gridTemplatesApi.validate(template.id)
      setMessage(result.valid ? 'Modèle validé et prêt à être chargé.' : result.issues.join(' · '))
      await loadTemplate(template.id)
      await loadTemplates()
    } finally {
      setBusy(false)
    }
  }, [template, loadTemplate, loadTemplates])

  const duplicateTemplate = useCallback(async () => {
    if (!template) return
    const copy = await gridTemplatesApi.duplicate(template.id)
    await loadTemplates()
    setSelectedId(copy.id)
  }, [template, loadTemplates])

  const renameTemplate = useCallback(async () => {
    if (!template) return
    const name = window.prompt('Nouveau nom du modèle', template.name)
    if (!name?.trim() || name.trim() === template.name) return
    await gridTemplatesApi.update(template.id, { name: name.trim() })
    await Promise.all([loadTemplate(template.id), loadTemplates()])
  }, [loadTemplate, loadTemplates, template])

  const archiveTemplate = useCallback(async () => {
    if (!template || !window.confirm(`Archiver « ${template.name} » ?`)) return
    await gridTemplatesApi.archive(template.id)
    setSelectedId(null)
    await loadTemplates()
  }, [template, loadTemplates])

  const restoreTemplate = useCallback(async () => {
    if (!template || template.status !== 'ARCHIVED' || !window.confirm(`Désarchiver « ${template.name} » ? Le modèle repassera en brouillon et devra être validé avant utilisation.`)) return
    await gridTemplatesApi.restore(template.id)
    setSelectedId(null)
    setTemplate(null)
    await loadTemplates()
    setMessage('Modèle désarchivé et replacé dans les brouillons actifs.')
  }, [template, loadTemplates])

  const deleteTemplate = useCallback(async () => {
    if (!template || template.status !== 'ARCHIVED') {
      setMessage('Archivez d’abord le modèle avant de pouvoir le supprimer définitivement.')
      return
    }
    const historyCount = template._count?.templateGenerations ?? 0
    const historyLabel = historyCount > 0
      ? `${historyCount} simulation(s) ou chargement(s) associé(s) seront également supprimés.`
      : 'Aucun historique de chargement n’est associé à ce modèle.'
    if (!window.confirm(
      `Supprimer définitivement « ${template.name} » avec son historique ?\n\n${historyLabel}\n\n` +
      'Les éléments déjà placés dans les grilles et l’historique AsRun seront conservés. En revanche, ces anciens chargements ne pourront plus être annulés.\n\nCette action est irréversible.'
    )) return
    setBusy(true)
    setMessage(null)
    try {
      const result = await gridTemplatesApi.permanentDelete(template.id, true)
      setSelectedId(null)
      setTemplate(null)
      await loadTemplates()
      setMessage(`Modèle supprimé avec ${result.deletedGenerations} entrée(s) d’historique. Les éléments déjà programmés ont été conservés.`)
    } catch (error: any) {
      setMessage(error.message)
    } finally {
      setBusy(false)
    }
  }, [template, loadTemplates])

  const handleDragEnd = useCallback(async (event: DragEndEvent) => {
    if (!canManage || !template || template.status === 'ARCHIVED' || !event.over) return
    const slot = template.templateSlots.find(item => item.id === event.active.id)
    if (!slot) return
    const targetDay = Math.min(Number(String(event.over.id).replace('template-day-', '')), 7 - slot.daySpan)
    const timeMinutes = timeToMinutes(slot.timeOfDay) + Math.round(event.delta.y / hourHeight * 60)
    await updateSlot(slot.id, { dayOffset: targetDay, timeOfDay: minutesToTime(timeMinutes) })
  }, [canManage, hourHeight, template, updateSlot])

  const handleZoomChange = useCallback((position: number) => {
    useStore.getState().setPxPerMinuteWeek(sliderToPpm(position, PPMW_MIN, PPMW_MAX))
  }, [])
  const handleZoomInput = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    handleZoomChange(Number(event.target.value))
  }, [handleZoomChange])
  const handleZoomStep = useCallback((direction: -1 | 1) => {
    handleZoomChange(ppmToSlider(useStore.getState().agendaView.pxPerMinuteWeek, PPMW_MIN, PPMW_MAX) + direction * 50)
  }, [handleZoomChange])
  const handleZoomOut = useCallback(() => handleZoomStep(-1), [handleZoomStep])
  const handleZoomIn = useCallback(() => handleZoomStep(1), [handleZoomStep])

  const handleSlotResize = useCallback(async (slotId: string, dayOffset: number, daySpan: number) => {
    await updateSlot(slotId, { dayOffset, daySpan })
  }, [updateSlot])

  const handleArchiveFilterChange = useCallback((value: 'active' | 'archived' | 'all') => setArchiveFilter(value), [])
  const handleTemplateSelect = useCallback((id: string) => setSelectedId(id), [])
  const handlePreviewClose = useCallback(() => setPreviewMode(false), [])
  const handlePreviewOpen = useCallback(() => setPreviewMode(true), [])
  const handleSmartDialogOpen = useCallback(() => setAssistDialog('SMART'), [])
  const handleRetimeDialogOpen = useCallback(() => setAssistDialog('RETIME'), [])
  const runRetime = useCallback(
    (anchorSlotIds: string[], apply: boolean) => gridTemplatesApi.retime(template!.id, { anchorSlotIds, apply }),
    [template],
  )
  // Le recalage réécrit les créneaux et repasse la validation : la vue doit
  // relire le modèle entier, version comprise.
  const reloadAfterRetime = useCallback(async () => {
    if (template) await loadTemplate(template.id)
    await loadTemplates()
  }, [loadTemplate, loadTemplates, template])
  const handleAssistDialogClose = useCallback(() => setAssistDialog(null), [])
  const handleSlotContextMenu = useCallback((slot: TemplateSlot, x: number, y: number) => {
    setSelectedSlotId(slot.id)
    setSlotMenu({ slot, x, y })
  }, [])
  const handleSlotSelect = useCallback((slotId: string, additive: boolean) => {
    if (additive) toggleSlotChecked(slotId)
    else {
      setSelectedSlotId(slotId)
      setInspectorMode('RULE')
    }
  }, [toggleSlotChecked])
  const handleSelectedSlotChange = useCallback((data: TemplateSlotWrite) => {
    if (selectedSlot) updateSlot(selectedSlot.id, data)
  }, [selectedSlot, updateSlot])
  const handleSlotMenuClose = useCallback(() => setSlotMenu(null), [])
  const slotMenuActions = useMemo(() => {
    if (!slotMenu) return []
    const archived = template?.status === 'ARCHIVED'
    return [
      { label: 'Modifier', disabled: !canManage || archived, onClick: () => setSelectedSlotId(slotMenu.slot.id) },
      { label: 'Dupliquer après ce créneau', disabled: !canManage || archived, onClick: () => { duplicateSlot(slotMenu.slot).catch(error => setMessage(error.message)) } },
      { label: 'Supprimer', danger: true, disabled: !canManage || archived, onClick: () => { deleteSpecificSlot(slotMenu.slot).catch(console.error) } },
      ...(checkedSlotIds.size > 0 ? [{
        label: `Supprimer les ${checkedSlotIds.size} créneaux sélectionnés`,
        danger: true,
        disabled: !canManage || archived,
        onClick: () => { deleteCheckedSlots().catch(console.error) },
      }] : []),
    ]
  }, [canManage, checkedSlotIds.size, deleteCheckedSlots, deleteSpecificSlot, duplicateSlot, slotMenu, template?.status])

  const templateGenerations = useMemo(
    () => generations.filter(item => item.templateId === template?.id),
    [generations, template?.id],
  )
  const latestAppliedGeneration = useMemo(
    () => templateGenerations.find(item => ['APPLIED', 'PARTIALLY_ROLLED_BACK'].includes(item.status)) ?? null,
    [templateGenerations],
  )
  const rollbackLatest = useCallback(async () => {
    if (!latestAppliedGeneration || !window.confirm(`Annuler le chargement v${latestAppliedGeneration.templateVersion} ? Les items diffusés ou à l’antenne resteront protégés.`)) return
    setBusy(true)
    setMessage(null)
    try {
      const result = await gridGenerationsApi.rollback(latestAppliedGeneration.id)
      setMessage(`${result.deleted} item(s) retiré(s), ${result.restored} restauré(s), ${result.preserved} protégé(s).`)
      await loadGenerations()
    } catch (error: any) {
      setMessage(error.message)
    } finally {
      setBusy(false)
    }
  }, [latestAppliedGeneration, loadGenerations])

  const templateStats = useMemo(() => {
    const slots = template?.templateSlots ?? []
    return {
      series: new Set(slots.map(item => item.seriesId).filter(Boolean)).size,
      reruns: slots.filter(item => ['PREMIERE_RERUN', 'SMART_RERUN', 'FRESH_RERUN', 'DESCENDING_RERUN', 'CURATED', 'MIRROR_RERUN'].includes(item.ruleType)).length,
      lives: slots.filter(item => item.ruleType === 'PREMIERE' && item.liveSourceId).length,
    }
  }, [template?.templateSlots])

  const handleWeekFilterChange = useCallback((value: WeekFilter) => setWeekFilter(value), [])

  // Semaine sélectionnée : ne garde que ce qui joue réellement cette
  // semaine-là. « Toutes » montre tout, y compris des créneaux qui ne
  // coexistent jamais en vrai (semaines disjointes) — le côte-à-côte ci-dessous
  // les sépare quand même pour que rien ne reste caché.
  const visibleSlots = useMemo(() => {
    const slots = template?.templateSlots ?? []
    if (weekFilter === 0) return slots
    return slots.filter(slot => !slot.cycleWeeks?.length || slot.cycleWeeks.includes(weekFilter))
  }, [template?.templateSlots, weekFilter])

  const slotLanes = useMemo(() => assignSlotLanes(visibleSlots), [visibleSlots])

  // Résolution des miroirs : on indexe TOUS les créneaux (pas seulement ceux du
  // filtre semaine), car un miroir peut viser une source masquée par le filtre.
  const slotsById = useMemo(() => {
    const map = new Map<string, TemplateSlot>()
    for (const slot of template?.templateSlots ?? []) map.set(slot.id, slot)
    return map
  }, [template?.templateSlots])

  if (!channelId) return <div className="tp-page-shell templates-view"><PageHeader title="Modèles de grille" /><div className="tp-empty-state">Sélectionnez une chaîne.</div></div>

  return (
    <div className="tp-page-shell templates-view">
      <PageHeader
        title={`Modèles · ${templates.length}`}
        actions={<button type="button" className="tp-button tp-button--primary" onClick={createTemplate} disabled={!canManage} title={!canManage ? 'Permission de gestion des modèles requise' : undefined}><Plus size={14} aria-hidden="true" /> Nouveau</button>}
      />
      <div className={`templates-workspace${previewMode ? ' is-preview' : ''}`}>
        <aside className="templates-nav" aria-label="Modèles disponibles">
          <div className="templates-nav__intro">
            <h2>Semaines réutilisables</h2>
            <p>Préparez, versionnez puis appliquez une structure éditoriale.</p>
          </div>
          <div className="tp-segmented templates-nav__filters" aria-label="Filtrer les modèles">
            {TEMPLATE_FILTERS.map(([value, label]) => (
              <TemplateFilterButton key={value} value={value} label={label} active={archiveFilter === value} onChange={handleArchiveFilterChange} />
            ))}
          </div>
          <div className="templates-nav__list">
            {templates.map(item => (
              <TemplateListButton key={item.id} template={item} selected={selectedId === item.id} onSelect={handleTemplateSelect} />
            ))}
            {templates.length === 0 && <div className="tp-empty-state templates-nav__empty">Aucun modèle.</div>}
          </div>
          {template && <TemplateVersionHistory generations={templateGenerations} currentVersion={template.version} />}
        </aside>

        <main className="templates-main">
          <div className="tp-toolbar templates-toolbar">
            <div className="templates-toolbar__identity">
              <div>
                <strong>{template?.name ?? 'Sélectionnez un modèle'}</strong>
                {template && <span>v{template.version} · modifié le {new Date(template.updatedAt).toLocaleDateString('fr-FR')}</span>}
              </div>
              {template && <span className={`templates-toolbar__status is-${template.status.toLowerCase()}`}>{template.status === 'VALIDATED' ? 'PRÊT' : template.status}</span>}
            </div>
            <div className="templates-toolbar__actions">
              {template && <div className="tp-zoom templates-zoom" title={`Échelle de la semaine : ${formatPpm(agendaView.pxPerMinuteWeek)}`}>
                <button type="button" className="tp-icon-button" aria-label="Réduire l’échelle" title="Réduire l’échelle" onClick={handleZoomOut}><Minus size={13} /></button>
                <input type="range" min={SLIDER_MIN} max={SLIDER_MAX} step={1} value={ppmToSlider(agendaView.pxPerMinuteWeek, PPMW_MIN, PPMW_MAX)} onChange={handleZoomInput} aria-label="Échelle de la semaine" aria-valuetext={formatPpm(agendaView.pxPerMinuteWeek)} />
                <button type="button" className="tp-icon-button" aria-label="Augmenter l’échelle" title="Augmenter l’échelle" onClick={handleZoomIn}><Plus size={13} /></button>
                <span>{formatPpm(agendaView.pxPerMinuteWeek)}</span>
              </div>}
              {template && (previewMode ? (
                <button type="button" className="tp-button" onClick={handlePreviewClose}>← Retour au modèle</button>
              ) : (
                <>
                  <button type="button" className="tp-button" onClick={handlePreviewOpen} disabled={template.status !== 'VALIDATED'}><Eye size={14} aria-hidden="true" /> Prévisualiser</button>
                  <button type="button" className="tp-button tp-button--primary" onClick={handlePreviewOpen} disabled={!canManage || template.status !== 'VALIDATED'}><CalendarRange size={14} aria-hidden="true" /> Appliquer sur une plage</button>
                </>
              ))}
            </div>
          </div>
          {template && !previewMode && <div className="templates-model-bar">
            <div className="templates-model-stats" aria-label="Résumé du modèle">
              <span>{template.templateSlots.length} créneaux</span>
              <span>{templateStats.series} séries</span>
              <span>{templateStats.reruns} rediffusions liées</span>
              <span>{templateStats.lives} directs</span>
            </div>
            <div className="tp-segmented templates-week-filter" aria-label="Filtrer par semaine du cycle">
              {WEEK_FILTERS.map(value => (
                <WeekFilterButton key={value} value={value} active={weekFilter === value} onChange={handleWeekFilterChange} />
              ))}
            </div>
            <div className="templates-model-actions">
              {template.status !== 'ARCHIVED' && <button type="button" className="tp-button tp-button--info" disabled={!canManage || busy} onClick={handleSmartDialogOpen}>Grille intelligente</button>}
              {template.status !== 'ARCHIVED' && <button type="button" className="tp-button" onClick={addSlot} disabled={!canManage}><Plus size={13} aria-hidden="true" /> Créneau</button>}
              {template.status !== 'ARCHIVED' && <button type="button" className="tp-button" disabled={!canManage || busy} onClick={handleRetimeDialogOpen} title="Ramener chaque fenêtre sur la durée réelle de son contenu">Recaler les durées</button>}
              {template.status !== 'ARCHIVED' && <button type="button" className="tp-button tp-button--primary" disabled={!canManage || busy} data-state={busy ? 'loading' : undefined} onClick={validateTemplate}><Check size={13} aria-hidden="true" /> Valider</button>}
              <details className="templates-more-actions">
                <summary className="tp-icon-button" aria-label="Plus d’actions" title="Plus d’actions"><MoreHorizontal size={15} /></summary>
                <div>
                  <button type="button" onClick={renameTemplate} disabled={!canManage || template.status === 'ARCHIVED'}><Pencil size={13} aria-hidden="true" /> Renommer</button>
                  <button type="button" onClick={duplicateTemplate} disabled={!canManage}><Copy size={13} aria-hidden="true" /> Dupliquer</button>
                  {latestAppliedGeneration && <button type="button" disabled={!canManage || busy} onClick={rollbackLatest}><RotateCcw size={13} aria-hidden="true" /> Rollback v{latestAppliedGeneration.templateVersion}</button>}
                  {template.status !== 'ARCHIVED' && <button type="button" onClick={archiveTemplate} disabled={!canManage}>Archiver le modèle</button>}
                  {template.status === 'ARCHIVED' && <button type="button" disabled={!canManage || busy} onClick={restoreTemplate}>Désarchiver</button>}
                  {template.status === 'ARCHIVED' && <button type="button" className="is-danger" disabled={!canManage || busy} onClick={deleteTemplate}>Supprimer avec l’historique</button>}
                </div>
              </details>
            </div>
          </div>}
          {message && <div className="tp-feedback templates-message" role="status">{message}</div>}

          {checkedSlotIds.size > 0 && (
            <div className="templates-selection" role="status">
              <span className="templates-selection__count">
                {checkedSlotIds.size} créneau{checkedSlotIds.size > 1 ? 'x' : ''} sélectionné{checkedSlotIds.size > 1 ? 's' : ''}
              </span>
              <span className="templates-selection__hint">Ctrl ou Maj + clic pour ajouter au lot</span>
              <button type="button" className="tp-button" onClick={clearCheckedSlots}>Annuler la sélection</button>
              <button
                type="button"
                className="tp-button tp-button--critical"
                disabled={!canManage || template?.status === 'ARCHIVED'}
                onClick={() => { deleteCheckedSlots().catch(console.error) }}
              >
                <Trash2 size={13} aria-hidden="true" />
                Supprimer la sélection
              </button>
            </div>
          )}
          {template && previewMode ? (
            <TemplatePreviewWorkspace template={template} channelId={channelId} canManage={canManage} hourHeight={hourHeight} scrollRef={canvasScrollRef} onHistoryChanged={loadGenerations} />
          ) : template ? (
            <DndContext sensors={sensors} collisionDetection={pointerWithin} onDragEnd={handleDragEnd}>
              <div ref={canvasScrollRef} className="templates-canvas-scroll">
                <div className="templates-canvas" style={templateCanvasVars(hourHeight)}>
                  <TemplateHourAxis hourHeight={hourHeight} />
                  {DAYS.map((day, index) => (
                    <TemplateDay
                      key={day}
                      day={day}
                      dayOffset={index}
                    />
                  ))}
                  <div className="template-blocks-layer">
                    {visibleSlots.map(slot => {
                      const lane = slotLanes.get(slot.id) ?? { lane: 0, laneCount: 1 }
                      // Débordement après minuit : rendu en tête du jour suivant
                      // comme prolongement du même créneau (daySpan 1 uniquement,
                      // les créneaux multi-jours se lisent déjà horizontalement).
                      const overflowMinutes = timeToMinutes(slot.timeOfDay) + slot.nominalDurationSec / 60 - 1440
                      return (
                        <Fragment key={slot.id}>
                          <TemplateBlock
                            slot={slot}
                            selected={selectedSlotId === slot.id}
                            checked={checkedSlotIds.has(slot.id)}
                            resizable={canManage && template.status !== 'ARCHIVED'}
                            onSelect={handleSlotSelect}
                            onContextMenu={handleSlotContextMenu}
                            onResize={handleSlotResize}
                            hourHeight={hourHeight}
                            lane={lane.lane}
                            laneCount={lane.laneCount}
                            slotsById={slotsById}
                          />
                          {slot.daySpan === 1 && overflowMinutes > 0 && slot.dayOffset < 6 && (
                            <TemplateContinuation
                              slot={slot}
                              dayOffset={slot.dayOffset + 1}
                              heightMinutes={Math.min(overflowMinutes, 1440)}
                              hourHeight={hourHeight}
                              selected={selectedSlotId === slot.id}
                              slotsById={slotsById}
                              onSelect={handleSlotSelect}
                              onContextMenu={handleSlotContextMenu}
                            />
                          )}
                        </Fragment>
                      )
                    })}
                  </div>
                </div>
              </div>
            </DndContext>
          ) : <div className="tp-empty-state">Créez ou sélectionnez un modèle.</div>}
        </main>

        {!previewMode && (
          <aside className="templates-inspector" aria-label="Règle du créneau">
            {template && <div className="templates-inspector__tabs" role="tablist" aria-label="Panneau latéral">
              <button type="button" role="tab" aria-selected={inspectorMode === 'GENERATION'} className={inspectorMode === 'GENERATION' ? 'is-active' : ''} onClick={() => setInspectorMode('GENERATION')}>Génération</button>
              <button type="button" role="tab" aria-selected={inspectorMode === 'RULE'} className={inspectorMode === 'RULE' ? 'is-active' : ''} disabled={!selectedSlot} onClick={() => setInspectorMode('RULE')}>Créneau</button>
            </div>}
            {template?.status === 'ARCHIVED' ? (
              <div className="templates-inspector__notice">Ce modèle est archivé. Vous pouvez le consulter, le dupliquer, le désarchiver ou le supprimer définitivement si aucun chargement encore appliqué ne dépend de lui.</div>
            ) : template && inspectorMode === 'GENERATION' ? (
              <TemplateGenerationSummary template={template} latest={latestAppliedGeneration} />
            ) : selectedSlot ? (
              <fieldset className="catalog-permission-fieldset" disabled={!canManage} title={!canManage ? 'Consultation seule — permission de gestion des modèles requise' : undefined}>
                <SlotEditor
                  slot={selectedSlot}
                  slots={template?.templateSlots ?? []}
                  programs={programs}
                  jingles={jingles}
                  adSpots={adSpots}
                  series={series}
                  liveSources={liveSources}
                  onChange={handleSelectedSlotChange}
                  onDelete={deleteSlot}
                />
              </fieldset>
            ) : <div className="templates-inspector__notice">Sélectionnez un bloc pour éditer sa règle.</div>}
          </aside>
        )}
      </div>

      {template && canManage && assistDialog === 'RETIME' && <TemplateRetimeDialog
        slots={template.templateSlots}
        onPlan={runRetime}
        onApplied={reloadAfterRetime}
        onClose={handleAssistDialogClose}
      />}
      {template && canManage && assistDialog === 'SMART' && <TemplateSmartFillDialog
        slots={template.templateSlots}
        series={series}
        onApply={addAssistedSlots}
        onClose={handleAssistDialogClose}
      />}
      {slotMenu && <ContextMenu
        x={slotMenu.x}
        y={slotMenu.y}
        onClose={handleSlotMenuClose}
        actions={slotMenuActions}
      />}
    </div>
  )
})

const TemplateFilterButton = memo(function TemplateFilterButton({
  value,
  label,
  active,
  onChange,
}: {
  value: 'active' | 'archived' | 'all'
  label: string
  active: boolean
  onChange: (value: 'active' | 'archived' | 'all') => void
}) {
  const handleClick = useCallback(() => onChange(value), [onChange, value])
  return <button type="button" className={`tp-segment${active ? ' is-active' : ''}`} onClick={handleClick} aria-pressed={active}>{label}</button>
})

const WeekFilterButton = memo(function WeekFilterButton({
  value,
  active,
  onChange,
}: {
  value: WeekFilter
  active: boolean
  onChange: (value: WeekFilter) => void
}) {
  const handleClick = useCallback(() => onChange(value), [onChange, value])
  const label = value === 0 ? 'Toutes' : `S${value}`
  const title = value === 0
    ? 'Afficher tous les créneaux, y compris ceux qui ne coexistent jamais en vrai'
    : `N’afficher que les créneaux posés en semaine ${value} du mois (un 5e lundi rejoue S1)`
  return <button type="button" className={`tp-segment${active ? ' is-active' : ''}`} onClick={handleClick} aria-pressed={active} title={title}>{label}</button>
})

const TemplateListButton = memo(function TemplateListButton({
  template,
  selected,
  onSelect,
}: {
  template: ScheduleGrid
  selected: boolean
  onSelect: (id: string) => void
}) {
  const handleClick = useCallback(() => onSelect(template.id), [onSelect, template.id])
  const status = template.status === 'ARCHIVED' ? 'Archivé'
    : template.status === 'ACTIVE' ? 'Actif'
    : template.status === 'VALIDATED' ? 'Validé'
    : 'Brouillon'
  const tone = template.status === 'ACTIVE' ? 'active'
    : template.status === 'VALIDATED' ? 'validated'
    : template.status === 'ARCHIVED' ? 'archived'
    : 'draft'
  return (
    <button type="button" onClick={handleClick} className={`templates-nav-item${selected ? ' is-selected' : ''}`} aria-current={selected ? 'true' : undefined}>
      <span className="templates-nav-item__name">
        {template.name}
        <span className={`templates-nav-item__badge is-${tone}`}>{status.toUpperCase()}</span>
      </span>
      <span className="templates-nav-item__meta">v{template.version} · {template._count?.templateSlots ?? 0} créneaux</span>
    </button>
  )
})

const TemplateVersionHistory = memo(function TemplateVersionHistory({ generations, currentVersion }: { generations: GridGeneration[]; currentVersion: number }) {
  return (
    <section className="templates-versions">
      <span className="template-panel-title">VERSIONS</span>
      <div className="templates-versions__list">
        <div className="templates-version is-current"><strong>v{currentVersion}</strong><span>version courante</span></div>
        {generations.slice(0, 3).map(item => (
          <div key={item.id} className="templates-version">
            <strong>v{item.templateVersion}</strong>
            <span>{item.status === 'ROLLED_BACK' ? 'annulée' : item.appliedAt ? new Date(item.appliedAt).toLocaleDateString('fr-FR') : 'appliquée'}</span>
          </div>
        ))}
        {generations.length === 0 && <p>Aucun chargement appliqué.</p>}
      </div>
    </section>
  )
})

const TemplateGenerationSummary = memo(function TemplateGenerationSummary({ template, latest }: {
  template: ScheduleGrid & { templateSlots: TemplateSlot[] }
  latest: GridGeneration | null
}) {
  return (
    <div className="template-generation-summary">
      <section>
        <span className="template-panel-title">GÉNÉRATION</span>
        <h2>Appliquer la semaine type</h2>
        <p>Le moteur choisit les épisodes, vérifie les médias et signale chaque conflit avant de poser le modèle sur la grille de diffusion de la semaine, journée vide par journée vide. Le modèle reste une simple prévisualisation tant qu'il n'est pas appliqué.</p>
      </section>
      <div className="template-generation-summary__facts">
        <span><strong>{template.templateSlots.length}</strong> créneaux dans la version v{template.version}</span>
        <span><Check size={14} aria-hidden="true" /> Items diffusés et à l’antenne protégés</span>
        <span><CalendarRange size={14} aria-hidden="true" /> Portée au jour ou à la semaine</span>
        {latest && <span><RotateCcw size={14} aria-hidden="true" /> Dernier chargement : v{latest.templateVersion}</span>}
      </div>
      <div className="tp-feedback">
        {template.status !== 'VALIDATED'
          ? 'Validez le modèle pour activer Prévisualiser et Appliquer sur une plage, en haut de l’écran.'
          : 'Utilisez Prévisualiser ou Appliquer sur une plage en haut de l’écran quand vous êtes prêt à publier.'}
      </div>
    </div>
  )
})

/** Un creneau dont la regle attend une source qui n'est pas renseignee. */
function isSlotIncomplete(slot: TemplateSlot): boolean {
  if (slot.ruleType === 'FIXED_PROGRAM')   return !slot.fixedProgramId
  // Un inédit se fonde sur une série, ou sur une source live pour un direct.
  if (slot.ruleType === 'PREMIERE')        return !slot.seriesId && !slot.liveSourceId
  if (slot.ruleType === 'PREMIERE_RERUN')  return !slot.seriesId
  // Un créneau libre ne sait poser que ce qu'on lui a donné.
  if (slot.ruleType === 'CURATED')         return (slot.entries?.length ?? 0) === 0
  // Une reprise miroir n'a de sens que vers un autre créneau du modèle.
  if (slot.ruleType === 'MIRROR_RERUN')    return !slot.mirrorSlotId
  return false
}

function slotTone(slot: TemplateSlot) {
  if (isSlotIncomplete(slot)) return 'blocking'
  if (slot.ruleType === 'PREMIERE' && slot.liveSourceId) return 'live'
  if (['PREMIERE_RERUN', 'SMART_RERUN', 'FRESH_RERUN', 'DESCENDING_RERUN', 'CURATED', 'MIRROR_RERUN'].includes(slot.ruleType)) return 'rerun'
  return 'anchor'
}

const TemplateDay = memo(function TemplateDay(props: {
  day: string
  dayOffset: number
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `template-day-${props.dayOffset}` })
  return (
    <section ref={setNodeRef} className={`template-day${isOver ? ' is-over' : ''}`}>
      <header className="template-day__header">{props.day}</header>
      <div className="template-day__timeline" />
    </section>
  )
})

// Remonte la chaîne des miroirs jusqu'au créneau réel qui porte un programme.
// Un garde de profondeur évite qu'une boucle de miroirs (a→b→a) fige l'UI.
function mirrorTargetOf(slot: TemplateSlot, byId: Map<string, TemplateSlot>, depth = 0): TemplateSlot | null {
  if (!slot.mirrorSlotId || depth > 6) return null
  const target = byId.get(slot.mirrorSlotId)
  if (!target) return null
  if (target.ruleType === 'MIRROR_RERUN') return mirrorTargetOf(target, byId, depth + 1) ?? target
  return target
}

function slotLabel(slot: TemplateSlot, byId?: Map<string, TemplateSlot>): string {
  // Une suite choisie à la main n'a pas de titre propre : elle s'annonce par
  // son premier programme, celui qui ouvrira le créneau à l'antenne.
  if (slot.ruleType === 'CURATED') {
    const entries = slot.entries ?? []
    if (entries.length === 0) return 'Suite à composer'
    const head = entries[0].program?.title ?? 'Programme'
    return entries.length > 1 ? `${head} +${entries.length - 1}` : head
  }
  if (slot.ruleType === 'MIRROR_RERUN') {
    if (!slot.mirrorSlotId) return 'Miroir à compléter'
    const target = byId ? mirrorTargetOf(slot, byId) : null
    // Sans le plan des créneaux (ou cible introuvable), on garde l'étiquette
    // générique ; sinon on annonce le programme réellement repris.
    return target ? `↺ ${slotLabel(target, byId)}` : 'Reprise miroir'
  }
  return slot.fixedProgram?.title ?? slot.series?.title ?? (
    slot.ruleType === 'PREMIERE' ? slot.liveSource?.name ?? 'Direct à compléter'
      : 'Règle à compléter'
  )
}

function slotLayoutStyle(
  slot: Pick<TemplateSlot, 'dayOffset' | 'daySpan' | 'timeOfDay' | 'nominalDurationSec'>,
  hourHeight: number,
  transform = 'none',
  lane = 0,
  laneCount = 1,
  // Un créneau qui franchit minuit est écrêté à 24h ici ; sa suite est rendue
  // en tête du jour suivant (voir TemplateContinuation).
  clipMinutes?: number,
) {
  const top = timeToMinutes(slot.timeOfDay) / 60 * hourHeight
  const durationMinutes = clipMinutes ?? slot.nominalDurationSec / 60
  const height = Math.max(22, durationMinutes / 60 * hourHeight)
  return {
    '--template-block-top': `${top}px`,
    '--template-block-height': `${height}px`,
    // Le jour donne la tranche de base ; quand plusieurs créneaux se
    // chevauchent réellement (même en dehors du filtre par semaine), la
    // tranche est redécoupée en voies côte à côte plutôt que de s'empiler.
    '--template-block-left': `calc((${slot.dayOffset} + ${lane} / ${laneCount}) * (100% / 7) + var(--space-2xs))`,
    '--template-block-width': `calc(${slot.daySpan} / ${laneCount} * (100% / 7) - var(--space-xs))`,
    '--template-block-transform': transform,
  } as CSSProperties
}

const TemplateBlock = memo(function TemplateBlock(props: {
  slot: TemplateSlot
  selected: boolean
  checked: boolean
  resizable: boolean
  onSelect: (id: string, additive: boolean) => void
  onContextMenu: (slot: TemplateSlot, x: number, y: number) => void
  onResize: (slotId: string, dayOffset: number, daySpan: number) => Promise<void>
  hourHeight: number
  lane: number
  laneCount: number
  slotsById: Map<string, TemplateSlot>
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: props.slot.id })
  const [resizeDraft, setResizeDraft] = useState<{ dayOffset: number; daySpan: number } | null>(null)
  const incomplete = isSlotIncomplete(props.slot)
  const visibleSlot = resizeDraft ? { ...props.slot, ...resizeDraft } : props.slot
  // Écrête l'affichage à 24h quand le créneau franchit minuit : la suite est
  // dessinée séparément en tête du jour suivant.
  const startMinutes = timeToMinutes(visibleSlot.timeOfDay)
  const durationMinutes = visibleSlot.nominalDurationSec / 60
  const clipMinutes = startMinutes + durationMinutes > 1440 ? 1440 - startMinutes : undefined
  const style = slotLayoutStyle(visibleSlot, props.hourHeight, transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : 'none', props.lane, props.laneCount, clipMinutes)

  useEffect(() => setResizeDraft(null), [props.slot.dayOffset, props.slot.daySpan])

  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    props.onSelect(props.slot.id, event.ctrlKey || event.metaKey || event.shiftKey)
  }, [props.onSelect, props.slot.id])
  const handleContextMenu = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    props.onContextMenu(props.slot, event.clientX, event.clientY)
  }, [props.onContextMenu, props.slot])

  const beginResize = useCallback((edge: 'start' | 'end', event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!props.resizable) return
    event.preventDefault()
    event.stopPropagation()
    props.onSelect(props.slot.id, false)
    const handle = event.currentTarget
    const pointerId = event.pointerId
    const originX = event.clientX
    const originalStart = props.slot.dayOffset
    const originalEnd = props.slot.dayOffset + props.slot.daySpan
    const layer = handle.closest('.template-blocks-layer')
    const dayWidth = layer ? layer.getBoundingClientRect().width / 7 : 1
    let latest = { dayOffset: originalStart, daySpan: props.slot.daySpan }

    const move = (pointerEvent: PointerEvent) => {
      const deltaDays = Math.round((pointerEvent.clientX - originX) / dayWidth)
      if (edge === 'start') {
        const dayOffset = Math.max(0, Math.min(originalEnd - 1, originalStart + deltaDays))
        latest = { dayOffset, daySpan: originalEnd - dayOffset }
      } else {
        const end = Math.max(originalStart + 1, Math.min(7, originalEnd + deltaDays))
        latest = { dayOffset: originalStart, daySpan: end - originalStart }
      }
      setResizeDraft(latest)
    }
    const finish = () => {
      handle.removeEventListener('pointermove', move)
      handle.removeEventListener('pointerup', finish)
      handle.removeEventListener('pointercancel', finish)
      if (handle.hasPointerCapture(pointerId)) handle.releasePointerCapture(pointerId)
      if (latest.dayOffset === originalStart && latest.daySpan === props.slot.daySpan) {
        setResizeDraft(null)
        return
      }
      props.onResize(props.slot.id, latest.dayOffset, latest.daySpan).catch(error => {
        setResizeDraft(null)
        console.error(error)
      })
    }

    handle.setPointerCapture(pointerId)
    handle.addEventListener('pointermove', move)
    handle.addEventListener('pointerup', finish)
    handle.addEventListener('pointercancel', finish)
  }, [props])

  const handleStartResize = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => beginResize('start', event), [beginResize])
  const handleEndResize = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => beginResize('end', event), [beginResize])
  const resizeWithKeyboard = useCallback((edge: 'start' | 'end', delta: number) => {
    const originalStart = props.slot.dayOffset
    const originalEnd = props.slot.dayOffset + props.slot.daySpan
    const dayOffset = edge === 'start' ? Math.max(0, Math.min(originalEnd - 1, originalStart + delta)) : originalStart
    const end = edge === 'end' ? Math.max(originalStart + 1, Math.min(7, originalEnd + delta)) : originalEnd
    const daySpan = end - dayOffset
    if (dayOffset !== originalStart || daySpan !== props.slot.daySpan) void props.onResize(props.slot.id, dayOffset, daySpan)
  }, [props])
  const handleStartKeyDown = useCallback((event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    resizeWithKeyboard('start', event.key === 'ArrowLeft' ? -1 : 1)
  }, [resizeWithKeyboard])
  const handleEndKeyDown = useCallback((event: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    resizeWithKeyboard('end', event.key === 'ArrowLeft' ? -1 : 1)
  }, [resizeWithKeyboard])

  return (
    <div
      ref={setNodeRef}
      className={`template-block is-${slotTone(props.slot)}${props.selected ? ' is-selected' : ''}${props.checked ? ' is-checked' : ''}${isDragging ? ' is-dragging' : ''}${incomplete ? ' is-incomplete' : ''}`}
      style={style}
    >
      <button
        type="button"
        {...listeners}
        {...attributes}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        className="template-block__body"
        title={incomplete ? 'Créneau incomplet : la règle attend une source' : undefined}
      >
        <TemplateBlockContent slot={visibleSlot} incomplete={incomplete} slotsById={props.slotsById} />
      </button>
      {props.resizable && <>
        <button type="button" className="template-block__resize is-start" aria-label="Étendre ou réduire depuis le premier jour" title="Étendre ou réduire depuis le premier jour" onPointerDown={handleStartResize} onKeyDown={handleStartKeyDown} />
        <button type="button" className="template-block__resize is-end" aria-label="Étendre ou réduire jusqu’au dernier jour" title="Étendre ou réduire jusqu’au dernier jour" onPointerDown={handleEndResize} onKeyDown={handleEndKeyDown} />
      </>}
    </div>
  )
})

const TemplateBlockContent = memo(function TemplateBlockContent({ slot, incomplete, slotsById }: { slot: TemplateSlot; incomplete: boolean; slotsById: Map<string, TemplateSlot> }) {
  const endDay = Math.min(6, slot.dayOffset + slot.daySpan - 1)
  const range = slot.daySpan > 1 ? `${DAYS[slot.dayOffset].slice(0, 3)}–${DAYS[endDay].slice(0, 3)} · ` : ''
  // Pour un miroir, on rappelle d'où vient la reprise (jour + heure de source)
  // afin qu'on lise en un coup d'œil quel passage est rejoué.
  const mirrorSource = slot.ruleType === 'MIRROR_RERUN' && slot.mirrorSlotId ? mirrorTargetOf(slot, slotsById) : null
  const rule = mirrorSource
    ? `miroir de ${DAYS[mirrorSource.dayOffset].slice(0, 3)} ${mirrorSource.timeOfDay.slice(0, 5)}`
    : slot.ruleType.replace(/_/g, ' ')
  return <>
    <span className="template-block__title">
      {incomplete && <span className="template-block__conflict" aria-label="Créneau incomplet">!</span>}
      {slot.timeOfDay.slice(0, 5)} · {slotLabel(slot, slotsById)}
    </span>
    <span className="template-block__meta">
      {range}{slot.airingKind === 'PREMIERE' ? 'INÉDIT' : slot.airingKind === 'RERUN' ? 'REDIFFUSION' : 'STANDARD'} · {rule}
    </span>
  </>
})

// Suite d'un créneau qui franchit minuit : même bloc, prolongé en tête du jour
// suivant. Cliquer sélectionne le créneau d'origine (« c'est juste un
// prolongement »), sans poignées de redimensionnement ni glisser-déposer.
const TemplateContinuation = memo(function TemplateContinuation(props: {
  slot: TemplateSlot
  dayOffset: number
  heightMinutes: number
  hourHeight: number
  selected: boolean
  slotsById: Map<string, TemplateSlot>
  onSelect: (id: string, additive: boolean) => void
  onContextMenu: (slot: TemplateSlot, x: number, y: number) => void
}) {
  const style = slotLayoutStyle(
    { dayOffset: props.dayOffset, daySpan: 1, timeOfDay: '00:00:00', nominalDurationSec: 0 },
    props.hourHeight, 'none', 0, 1, props.heightMinutes,
  )
  const handleClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    props.onSelect(props.slot.id, event.ctrlKey || event.metaKey || event.shiftKey)
  }, [props.onSelect, props.slot.id])
  const handleContextMenu = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()
    props.onContextMenu(props.slot, event.clientX, event.clientY)
  }, [props.onContextMenu, props.slot])
  return (
    <div className={`template-block is-${slotTone(props.slot)} is-continuation${props.selected ? ' is-selected' : ''}`} style={style}>
      <button type="button" onClick={handleClick} onContextMenu={handleContextMenu} className="template-block__body" title="Suite du créneau de la veille">
        <span className="template-block__title">↳ {slotLabel(props.slot, props.slotsById)}</span>
        <span className="template-block__meta">suite du {DAYS[props.slot.dayOffset].slice(0, 3)} {props.slot.timeOfDay.slice(0, 5)}</span>
      </button>
    </div>
  )
})

/**
 * Semaines du cycle mensuel d'un créneau. Quatre cases, et surtout les
 * prochaines dates de passage : l'exploitant voit le résultat de la règle
 * (5e lundi → semaine 1) au lieu de devoir la refaire de tête.
 */
const SlotCycleWeeksField = memo(function SlotCycleWeeksField(props: {
  cycleWeeks: number[] | null | undefined
  onChange: (cycleWeeks: number[]) => void
}) {
  const { onChange } = props
  const weeks = useMemo(() => props.cycleWeeks ?? [], [props.cycleWeeks])
  // Lu une fois au montage : une date de référence au jour près suffit.
  const [todayKey] = useState(() => broadcastDayKey(getServerNow()))
  const upcoming = useMemo(() => upcomingCycleMondays(weeks, todayKey, 5), [weeks, todayKey])
  const fifthMonday = useMemo(() => nextFifthMonday(todayKey), [todayKey])
  const legacyFifthOnly = isFifthWeekOnly(weeks)
  const toggleWeek = useCallback((week: number, checked: boolean) => {
    // La case 5 héritée disparaît dès qu'on touche aux semaines.
    const current = weeks.filter(item => item !== 5)
    onChange(checked ? [...current, week].sort((a, b) => a - b) : current.filter(item => item !== week))
  }, [onChange, weeks])
  return (
    <Field label="Semaines du mois">
      <div className="template-slot-editor__weeks">
        {MONTH_CYCLE_WEEK_NUMBERS.map(week => (
          <label key={week} className="template-slot-editor__week">
            <input type="checkbox" checked={weeks.includes(week)} onChange={event => toggleWeek(week, event.target.checked)} />
            S{week}
          </label>
        ))}
      </div>
      {legacyFifthOnly ? (
        <small className="template-slot-editor__weeks-warning">
          Ce créneau n’était coché qu’en 5e semaine, qui n’existe plus : il ne serait jamais posé. Cochez une semaine de S1 à S4.
        </small>
      ) : (
        <small>
          {weeks.length === 0 ? 'Aucune case cochée : toutes les semaines. ' : ''}
          Prochains passages : semaines du{' '}
          {upcoming.map((item, index) => (
            <Fragment key={item.key}>
              {index > 0 && ', '}
              {item.label}
              {item.fifthMonday && <strong> (5e lundi → S1)</strong>}
            </Fragment>
          ))}…
        </small>
      )}
      <small>
        S1 = la semaine dont le lundi tombe du 1er au 7 du mois, puis S2, S3, S4. Quand un mois a un 5e lundi
        {fifthMonday ? ` (prochain : ${fifthMonday.label})` : ''}, cette semaine rejoue S1. Deux créneaux au même horaire avec des semaines différentes ne se gênent pas.
      </small>
    </Field>
  )
})

const SlotEditor = memo(function SlotEditor(props: {
  slot: TemplateSlot
  slots: TemplateSlot[]
  programs: Program[]
  jingles: RollProgram[]
  adSpots: RollSpot[]
  series: Series[]
  liveSources: LiveSource[]
  onChange: (data: TemplateSlotWrite) => void
  onDelete: () => void
}) {
  const averageDurationSec = useMemo(() => {
    if (!props.slot.seriesId) return null
    return averageProgramDurationSec(props.programs, program => program.seriesId === props.slot.seriesId)
  }, [props.programs, props.slot.seriesId])
  const slotsById = useMemo(() => new Map(props.slots.map(slot => [slot.id, slot])), [props.slots])
  // Le rôle éditorial découle de la règle : un créneau inédit pose un inédit,
  // une règle de reprise pose une rediffusion. Il n'est plus réglable à part.
  const setRule = (ruleType: TemplateRuleType) => props.onChange({
    ruleType,
    airingKind: ruleType === 'PREMIERE' ? 'PREMIERE' : ruleType === 'FIXED_PROGRAM' ? 'STANDARD' : 'RERUN',
    fixedProgramId: null,
    seriesId: null,
    liveSourceId: null,
    mirrorSlotId: null,
    entryProgramIds: [],
  })
  return (
    <div className="template-slot-editor">
      <div className="template-slot-editor__header">
        <h2>Règle du créneau</h2>
        <p>La durée définit une fenêtre éditoriale : plusieurs épisodes peuvent s’enchaîner, puis le reliquat est comblé jusqu’au programme suivant.</p>
      </div>
      <span className="template-panel-title">PLACEMENT</span>
      <Field label="Jour">
        <select className="tp-select" value={props.slot.dayOffset} onChange={event => {
          const dayOffset = Number(event.target.value)
          props.onChange({ dayOffset, daySpan: Math.min(props.slot.daySpan, 7 - dayOffset) })
        }}>
          {DAYS.map((day, index) => <option key={day} value={index}>{day}</option>)}
        </select>
      </Field>
      <Field label="Jours couverts">
        <select className="tp-select" value={props.slot.daySpan} onChange={event => props.onChange({ daySpan: Number(event.target.value) })}>
          {Array.from({ length: 7 - props.slot.dayOffset }, (_, index) => index + 1).map(span => (
            <option key={span} value={span}>
              {span === 1 ? '1 jour' : `${span} jours · jusqu’à ${DAYS[props.slot.dayOffset + span - 1]}`}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Début">
        <input className="tp-input tp-input--data" type="time" step="1" value={props.slot.timeOfDay} onChange={event => props.onChange({ timeOfDay: event.target.value.length === 5 ? `${event.target.value}:00` : event.target.value })} />
      </Field>
      <Field label={`Durée du créneau · ${formatDurationShort(props.slot.nominalDurationSec)}`}>
        <div className="template-duration-input">
          <label>
            <input
              className="tp-input tp-input--data"
              type="number"
              min="0"
              step="1"
              value={Math.floor(props.slot.nominalDurationSec / 60)}
              onChange={event => {
                const minutes = Math.max(0, Math.round(Number(event.target.value)))
                props.onChange({ nominalDurationSec: Math.max(1, minutes * 60 + (props.slot.nominalDurationSec % 60)) })
              }}
            />
            <span>min</span>
          </label>
          <label>
            <input
              className="tp-input tp-input--data"
              type="number"
              min="0"
              max="59"
              step="1"
              value={props.slot.nominalDurationSec % 60}
              onChange={event => {
                const seconds = Math.min(59, Math.max(0, Math.round(Number(event.target.value))))
                props.onChange({ nominalDurationSec: Math.max(1, Math.floor(props.slot.nominalDurationSec / 60) * 60 + seconds) })
              }}
            />
            <span>s</span>
          </label>
        </div>
        {averageDurationSec && <small>Moyenne des épisodes : {Math.round(averageDurationSec / 60)} min. Allongez la fenêtre pour enchaîner plusieurs épisodes.</small>}
      </Field>
      <Field label="Rendez-vous d’antenne">
        <label className="template-slot-editor__anchor">
          <input
            type="checkbox"
            checked={props.slot.anchored}
            onChange={event => props.onChange({ anchored: event.target.checked })}
          />
          <span>Heure de début tenue à la seconde</span>
        </label>
        <small>
          {props.slot.anchored
            ? 'Ce créneau ne bouge pas : un programme qui déborde dessus garde son dépassement, signalé avant le dépôt.'
            : 'La durée réelle du contenu fait foi : ce créneau est décalé quand ce qui le précède déborde de sa fenêtre.'}
        </small>
      </Field>
      {['PREMIERE', 'SMART_RERUN', 'FRESH_RERUN', 'DESCENDING_RERUN', 'CURATED'].includes(props.slot.ruleType) && <Field label="Espacement entre les épisodes (minutes)">
        <input className="tp-input tp-input--data" type="number" min="0" step="1" value={Math.round((props.slot.episodeSpacingSec ?? 0) / 60)} onChange={event => props.onChange({ episodeSpacingSec: Math.max(0, Number(event.target.value)) * 60 })} />
        <small>Cet intervalle reste vide dans le modèle : il est comblé sur la grille de diffusion, par élément d’antenne ou infoservice.</small>
      </Field>}
      <Field label="Calage dans le créneau">
        <select className="tp-select" value={props.slot.contentAlignment} onChange={event => props.onChange({ contentAlignment: event.target.value as TemplateSlot['contentAlignment'] })}>
          <option value="START">Collé au début — le programme démarre à l’heure du créneau</option>
          <option value="END">Collé à la fin — le programme se termine à la fin du créneau</option>
        </select>
        <small>
          {props.slot.contentAlignment === 'END'
            ? 'Le contenu est repoussé pour finir sur l’heure de fin ; le creux tombe avant lui. Sans effet s’il remplit déjà la fenêtre.'
            : 'Le contenu démarre à l’heure du créneau ; le creux éventuel tombe après lui.'}
        </small>
      </Field>
      <SlotCycleWeeksField cycleWeeks={props.slot.cycleWeeks} onChange={cycleWeeks => props.onChange({ cycleWeeks })} />

      <span className="template-panel-title">SOURCE</span>
      <Field label="Type de règle">
        <select className="tp-select" value={props.slot.ruleType} onChange={event => setRule(event.target.value as TemplateRuleType)}>
          <option value="PREMIERE">Inédit — la fiche du jour est créée au dépôt</option>
          <option value="PREMIERE_RERUN">Rediffusion de l’inédit — suit le dernier inédit de la série</option>
          <option value="SMART_RERUN">Rediffusion libre — rotation dans une série</option>
          <option value="FRESH_RERUN">Nouveautés — épisodes récents en avant</option>
          <option value="DESCENDING_RERUN">Rediffusion antichronologique — du plus récent au plus ancien</option>
          <option value="CURATED">Créneau libre — suite de programmes choisie</option>
          <option value="MIRROR_RERUN">Reprise miroir — même épisode qu’un autre créneau</option>
          <option value="FIXED_PROGRAM">Programme fixe / billboard</option>
        </select>
        <small>
          {props.slot.ruleType === 'PREMIERE' && 'Le créneau fabrique la fiche du jour : épisode de la série, ou direct si une source live est choisie.'}
          {props.slot.ruleType === 'PREMIERE_RERUN' && 'Le créneau reprend l’inédit le plus récent de la série indiquée. Dès qu’un nouvel inédit est posé, c’est lui qui est rediffusé.'}
          {props.slot.ruleType === 'SMART_RERUN' && 'Tous les épisodes actifs et pourvus d’un média peuvent être repris, même sans historique de diffusion. Le jour même et la veille sont exclus ; Recalculer avance vers le lot suivant. La rotation parcourt tout le vivier avant de rejouer quoi que ce soit : les épisodes jamais diffusés passent en premier, sans traitement de faveur pour les plus récents.'}
          {props.slot.ruleType === 'FRESH_RERUN' && 'Même vivier que la rediffusion libre, mais un épisode encore dans les sept jours qui suivent son inédit passe devant tout le reste, deux reprises au plus. Sans nouveauté disponible, le créneau retombe sur la rotation ordinaire. Réservez-le à un créneau vitrine : sur tous les créneaux d’une série alimentée chaque jour, le reste du catalogue ne sortirait jamais.'}
          {props.slot.ruleType === 'DESCENDING_RERUN' && 'La série est déroulée du plus récent au plus ancien, un cran par occurrence, puis la descente reprend au plus récent. Un épisode plus récent remet le tour à zéro et la descente repart de lui.'}
          {props.slot.ruleType === 'CURATED' && 'Vous composez la suite, le créneau la rejoue telle quelle à chaque occurrence : un best of de rediffusion que rien n’arbitre à votre place.'}
          {props.slot.ruleType === 'MIRROR_RERUN' && 'Reprend l’épisode exact déposé au dernier passage du créneau miroir, quelle que soit sa propre règle : utile pour une rediffusion fidèle d’un bloc déjà diffusé (ex: lundi qui reprend le vendredi précédent).'}
          {props.slot.ruleType === 'FIXED_PROGRAM' && 'Une fiche nommée à l’avance, immuable d’une semaine sur l’autre.'}
        </small>
      </Field>
      {props.slot.ruleType === 'MIRROR_RERUN' && <Field label="Créneau miroir">
        <select className="tp-select" value={props.slot.mirrorSlotId ?? ''} onChange={event => props.onChange({ mirrorSlotId: event.target.value || null })}>
          <option value="">Sélectionner…</option>
          {props.slots.filter(item => item.id !== props.slot.id).map(item => (
            <option key={item.id} value={item.id}>
              {DAYS[item.dayOffset]} {item.timeOfDay.slice(0, 5)} · {slotLabel(item, slotsById)}
            </option>
          ))}
        </select>
        <small>À chaque génération, ce créneau reprend l’épisode réellement déposé au dernier passage du créneau choisi ci-dessus, quel que soit le sous-créneau qui l’a produit.</small>
      </Field>}
      {props.slot.ruleType === 'FIXED_PROGRAM' && <Field label="Programme">
        <select className="tp-select" value={props.slot.fixedProgramId ?? ''} onChange={event => {
          const fixedProgramId = event.target.value || null
          const program = props.programs.find(item => item.id === fixedProgramId)
          props.onChange({
            fixedProgramId,
            ...(program ? { nominalDurationSec: program.blockDurationSec ?? program.totalDurationSec ?? program.durationSec } : {}),
          })
        }}>
          <option value="">Sélectionner…</option>
          {props.programs.flatMap(program => [
            <option key={program.id} value={program.id}>{program.title}{(program.partCount ?? 1) > 1 ? ` · ${program.partCount} parties` : ''}</option>,
            ...(program.parts ?? []).map(part => <option key={part.id} value={part.id}>↳ Partie {part.partNumber} · {part.title}</option>),
          ])}
        </select>
      </Field>}
      {!['FIXED_PROGRAM', 'MIRROR_RERUN'].includes(props.slot.ruleType) && <Field label={props.slot.ruleType === 'CURATED' ? 'Série / émission (facultatif)' : 'Série / émission'}>
        <select className="tp-select" value={props.slot.seriesId ?? ''} onChange={async event => {
          const seriesId = event.target.value || null
          const detail = seriesId ? await seriesApi.getById(seriesId) : null
          const episodes = detail ? Object.values(detail.seasons).flat() : []
          const average = averageProgramDurationSec(episodes, () => true)
          props.onChange({ seriesId, ...(average ? { nominalDurationSec: average } : {}) })
        }}>
          <option value="">{props.slot.ruleType === 'CURATED' ? 'Tout le catalogue' : 'Sélectionner…'}</option>
          {props.series.map(series => <option key={series.id} value={series.id}>{series.title}</option>)}
        </select>
        {props.slot.ruleType === 'PREMIERE' && <small>Les épisodes inédits sans média encore livré sont acceptés : le créneau se génère quand même, avec un rappel « média à associer » jusqu’à l’ajout du fichier.</small>}
        {props.slot.ruleType === 'PREMIERE_RERUN' && <small>La rediffusion suit la série, pas un créneau : elle reprend automatiquement l’inédit le plus récent, même s’il vient d’une semaine antérieure.</small>}
        {props.slot.ruleType === 'CURATED' && <small>La série ne fait que restreindre la liste ci-dessous : la suite retenue fait foi, qu’elle reste dans une série ou qu’elle pioche partout.</small>}
      </Field>}
      {props.slot.ruleType === 'PREMIERE' && <>
        <Field label="Source live (facultatif)">
          <select className="tp-select" value={props.slot.liveSourceId ?? ''} onChange={event => props.onChange({ liveSourceId: event.target.value || null })}>
            <option value="">Aucune — épisode de la série</option>
            {props.liveSources.map(source => <option key={source.id} value={source.id}>{source.name} · {source.sourceType}</option>)}
          </select>
          <small>Avec une source live, l’inédit se tourne à l’antenne : la fiche du jour est créée au dépôt et son média rattaché après la diffusion, ce qui la rend rediffusable ensuite.</small>
        </Field>
        {props.slot.liveSourceId && <Field label="Démarrage">
          <select className="tp-select" value={props.slot.liveStartMode} onChange={event => props.onChange({ liveStartMode: event.target.value as TemplateSlot['liveStartMode'] })}>
            <option value="AUTO">Automatique à l’heure du créneau</option>
            <option value="MANUAL">Manuel — lancé par le régisseur</option>
          </select>
        </Field>}
        {props.slot.liveSourceId && <Field label="Fin">
          <select className="tp-select" value={props.slot.liveEndMode} onChange={event => props.onChange({ liveEndMode: event.target.value as TemplateSlot['liveEndMode'] })}>
            <option value="AUTO">Automatique à l’heure du créneau</option>
            <option value="MANUAL">Manuelle — coupée par le régisseur</option>
          </select>
        </Field>}
      </>}

      {props.slot.ruleType === 'CURATED' && (
        <CuratedSequenceEditor slot={props.slot} programs={props.programs} onChange={props.onChange} />
      )}

      <span className="template-panel-title">ÉLÉMENTS D’ANTENNE</span>
      <RollPicker
        label="Avant chaque programme"
        jingles={props.jingles}
        adSpots={props.adSpots}
        programId={props.slot.preRollProgramId}
        adSpotId={props.slot.preRollAdSpotId}
        onChange={value => props.onChange({ preRollProgramId: value.programId, preRollAdSpotId: value.adSpotId })}
      />
      <RollPicker
        label="Après chaque programme"
        jingles={props.jingles}
        adSpots={props.adSpots}
        programId={props.slot.postRollProgramId}
        adSpotId={props.slot.postRollAdSpotId}
        onChange={value => props.onChange({ postRollProgramId: value.programId, postRollAdSpotId: value.adSpotId })}
      />
      <small className="template-slot-editor__hint">
        Ces éléments encadrent chaque programme posé par le créneau, suite comprise, et leur durée est prise sur la fenêtre : la place restante pour les programmes diminue d’autant.
      </small>

      <button type="button" className="tp-button tp-button--danger-outline" onClick={props.onDelete}>Supprimer ce créneau</button>
    </div>
  )
})

/**
 * Choix d'un pre-roll ou d'un post-roll. Habillages et spots partagent une seule
 * liste : le créneau n'en accepte qu'un par bout, et un sélecteur unique rend
 * cette exclusion impossible à contourner.
 */
const RollPicker = memo(function RollPicker(props: {
  label: string
  jingles: RollProgram[]
  adSpots: RollSpot[]
  programId: string | null
  adSpotId: string | null
  onChange: (value: { programId: string | null; adSpotId: string | null }) => void
}) {
  const value = props.programId ? `program:${props.programId}` : props.adSpotId ? `spot:${props.adSpotId}` : ''
  return (
    <Field label={props.label}>
      <select className="tp-select" value={value} onChange={event => {
        const [kind, id] = event.target.value.split(':')
        props.onChange({
          programId: kind === 'program' ? id : null,
          adSpotId: kind === 'spot' ? id : null,
        })
      }}>
        <option value="">Aucun</option>
        {props.jingles.length > 0 && <optgroup label="Éléments d’antenne">
          {props.jingles.map(program => (
            <option key={program.id} value={`program:${program.id}`}>{program.title} · {formatDurationShort(program.durationSec)}</option>
          ))}
        </optgroup>}
        {props.adSpots.length > 0 && <optgroup label="Spot publicitaire">
          {props.adSpots.map(spot => (
            <option key={spot.id} value={`spot:${spot.id}`}>{formatAdSpotLabel(spot)} · {formatDurationShort(spot.durationSec)}</option>
          ))}
        </optgroup>}
      </select>
      {props.jingles.length === 0 && props.adSpots.length === 0 && (
        <small>Aucun élément d’antenne avec un média diffusable ni spot actif n’est disponible sur cette chaîne.</small>
      )}
    </Field>
  )
})

/** Durée courte d'un habillage ou d'un spot, en secondes tant qu'elle tient. */
function formatDurationShort(durationSec: number): string {
  return durationSec < 60 ? `${durationSec} s` : `${Math.floor(durationSec / 60)} min ${String(durationSec % 60).padStart(2, '0')}`
}

/**
 * Composition de la suite d'un créneau libre. L'ordre affiché est l'ordre
 * d'antenne : c'est la seule information que le moteur reprend, il ne réarbitre
 * rien. Le cumul face à la fenêtre du créneau est montré en clair, faute de
 * quoi on ne découvrirait qu'à la simulation que la fin de la suite déborde.
 */
const CuratedSequenceEditor = memo(function CuratedSequenceEditor(props: {
  slot: TemplateSlot
  programs: Program[]
  onChange: (data: TemplateSlotWrite) => void
}) {
  const [search, setSearch] = useState('')
  const entries = props.slot.entries ?? []
  const chosenIds = useMemo(() => entries.map(entry => entry.programId), [entries])
  const programById = useMemo(() => new Map(props.programs.map(program => [program.id, program])), [props.programs])

  const durationOf = useCallback((entry: TemplateSlotEntry) => {
    const program = programById.get(entry.programId)
    return program
      ? program.blockDurationSec ?? program.totalDurationSec ?? program.durationSec
      : entry.program?.durationSec ?? 0
  }, [programById])
  const titleOf = useCallback((entry: TemplateSlotEntry) => (
    programById.get(entry.programId)?.title ?? entry.program?.title ?? 'Programme retiré du catalogue'
  ), [programById])

  const spacingSec = Math.max(0, props.slot.episodeSpacingSec ?? 0)
  const totalSec = entries.reduce((total, entry) => total + durationOf(entry), 0)
    + spacingSec * Math.max(0, entries.length - 1)
  const overflowSec = totalSec - props.slot.nominalDurationSec

  const commit = useCallback((nextIds: string[]) => props.onChange({ entryProgramIds: nextIds }), [props.onChange])
  const move = useCallback((index: number, delta: number) => {
    const next = [...chosenIds]
    const target = index + delta
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    commit(next)
  }, [chosenIds, commit])

  const needle = search.trim().toLowerCase()
  const choices = props.programs.filter(program => (
    !chosenIds.includes(program.id)
    && (!props.slot.seriesId || program.seriesId === props.slot.seriesId)
    && (needle === '' || program.title.toLowerCase().includes(needle))
  )).slice(0, 40)

  return <>
    <span className="template-panel-title">SUITE DE PROGRAMMES</span>
    <div className="template-sequence">
      {entries.length === 0
        ? <p className="template-sequence__empty">Aucun programme choisi : le créneau ne posera rien tant que la suite est vide.</p>
        : <ol className="template-sequence__list">
            {entries.map((entry, index) => (
              <li key={entry.programId} className="template-sequence__item">
                <span className="template-sequence__rank">{index + 1}</span>
                <span className="template-sequence__title">{titleOf(entry)}</span>
                <span className="template-sequence__duration">{Math.round(durationOf(entry) / 60)} min</span>
                <button type="button" className="tp-icon-button" title="Monter" aria-label="Monter" disabled={index === 0} onClick={() => move(index, -1)}>↑</button>
                <button type="button" className="tp-icon-button" title="Descendre" aria-label="Descendre" disabled={index === entries.length - 1} onClick={() => move(index, 1)}>↓</button>
                <button type="button" className="tp-icon-button" title="Retirer" aria-label="Retirer" onClick={() => commit(chosenIds.filter(id => id !== entry.programId))}>
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ol>}
      {entries.length > 0 && (
        <p className={`template-sequence__total${overflowSec > 0 ? ' is-overflow' : ''}`}>
          {Math.round(totalSec / 60)} min de suite pour une fenêtre de {Math.round(props.slot.nominalDurationSec / 60)} min
          {overflowSec > 0 && ` · les derniers programmes ne tiennent pas`}
        </p>
      )}
      <input
        className="tp-input"
        type="search"
        value={search}
        placeholder={props.slot.seriesId ? 'Chercher dans la série…' : 'Chercher dans le catalogue…'}
        onChange={event => setSearch(event.target.value)}
      />
      <div className="template-sequence__choices">
        {choices.length === 0
          ? <p className="template-sequence__empty">Aucun programme disponible pour cette recherche.</p>
          : choices.map(program => (
              <button
                key={program.id}
                type="button"
                className="template-sequence__choice"
                onClick={() => commit([...chosenIds, program.id])}
              >
                <Plus size={14} />
                <span>{program.title}</span>
                <span className="template-sequence__duration">
                  {Math.round((program.blockDurationSec ?? program.totalDurationSec ?? program.durationSec) / 60)} min
                </span>
              </button>
            ))}
      </div>
    </div>
  </>
})

const Field = memo(function Field(props: { label: string; children: React.ReactNode }) {
  return <label className="template-field"><span className="tp-field-label">{props.label}</span>{props.children}</label>
})
